import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { ArrowLeft, Send, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { format } from 'date-fns';

export default function ConversationDetail({ conversation, onBack, projectId }) {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const { getAuthHeaders, API, user } = useAuth();

  useEffect(() => {
    fetchComments();
  }, [conversation.id]);

  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API}/comments/${conversation.id}`, getAuthHeaders());
      setComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      await axios.post(`${API}/comments/${conversation.id}`, { text: newComment }, getAuthHeaders());
      setNewComment('');
      fetchComments();
    } catch (error) {
      console.error('Failed to add comment:', error);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      {/* Left: Conversation Content */}
      <div className="lg:col-span-3">
        <Button
          onClick={onBack}
          variant="ghost"
          size="sm"
          className="mb-4 text-slate-600 hover:text-slate-900"
          data-testid="back-to-conversations-button"
        >
          <ArrowLeft size={18} className="mr-2" />
          Back to Conversations
        </Button>

        <div className="card p-8">
          <h2 className="text-3xl font-heading font-bold text-slate-900 mb-4">{conversation.title}</h2>
          <div className="text-xs text-slate-500 font-mono mb-6">
            Synced on {format(new Date(conversation.synced_at), 'MMM dd, yyyy \'at\' h:mm a')}
          </div>
          <div className="prose prose-slate max-w-none">
            <div className="whitespace-pre-wrap text-slate-700 leading-relaxed">
              {conversation.content}
            </div>
          </div>
        </div>
      </div>

      {/* Right: Comments */}
      <div className="lg:col-span-2">
        <div className="sticky top-24">
          <div className="card p-6">
            <h3 className="text-xl font-heading font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <MessageSquare size={20} />
              Comments
            </h3>

            <div className="space-y-4 mb-6 max-h-[500px] overflow-y-auto">
              {loading ? (
                <div className="text-center py-8 text-slate-500">Loading comments...</div>
              ) : comments.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <MessageSquare className="mx-auto mb-2 text-slate-300" size={32} />
                  <p className="text-sm">No comments yet. Start the discussion!</p>
                </div>
              ) : (
                comments.map((comment) => (
                  <div key={comment.id} className="bg-slate-50 rounded-md p-4" data-testid="comment-item">
                    <div className="flex items-start justify-between mb-2">
                      <span className="font-medium text-sm text-slate-900">{comment.user_name}</span>
                      <span className="text-xs text-slate-500">
                        {format(new Date(comment.created_at), 'MMM dd, h:mm a')}
                      </span>
                    </div>
                    <p className="text-sm text-slate-700">{comment.text}</p>
                  </div>
                ))
              )}
            </div>

            <form onSubmit={handleAddComment} className="space-y-3">
              <Textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add your comment or insight..."
                rows={3}
                data-testid="comment-input"
              />
              <Button
                type="submit"
                className="btn-primary w-full flex items-center justify-center gap-2"
                disabled={!newComment.trim()}
                data-testid="comment-submit-button"
              >
                <Send size={16} />
                Add Comment
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
